import React, { useState } from 'react';
import { UI_THEME } from '../ui/theme';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import MinhasSolicitacoes from '../components/MinhasSolicitacoes';
import FilaSolicitacoes from '../components/FilaSolicitacoes';

type Aba = 'fila' | 'minhas';

/**
 * Solicitações de alteração. Quem trata (permissão TRATAR_SOLICITACAO_ALTERACAO) vê a fila da equipe;
 * quem abre (CREATE_SOLICITACAO_ALTERACAO) vê as próprias. Com as duas permissões, as duas abas.
 */
const SolicitacoesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const isSaas = UI_THEME === 'saas';
  const podeTratar = hasPermission(Permission.TRATAR_SOLICITACAO_ALTERACAO);
  const podeCriar = hasPermission(Permission.CREATE_SOLICITACAO_ALTERACAO);
  const [aba, setAba] = useState<Aba>(podeTratar ? 'fila' : 'minhas');
  const abaAtiva: Aba = aba === 'fila' && !podeTratar ? 'minhas' : aba === 'minhas' && !podeCriar ? 'fila' : aba;

  const hero = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accent = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const tab = (ativa: boolean) =>
    `rounded-full px-4 py-1.5 text-sm font-semibold transition ${
      ativa
        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
        : 'border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800'
    }`;

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border p-6 shadow-sm ${hero}`}>
        {podeTratar && (
          <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accent}`}>
            {abaAtiva === 'fila' ? 'Equipe' : 'Parlamentar'}
          </p>
        )}
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
          {podeTratar ? 'Suporte e solicitações' : 'Fale com a equipe'}
        </h1>
        <p className="mt-2 text-base leading-7 text-slate-600 dark:text-slate-300">
          {abaAtiva === 'fila'
            ? 'Pedidos de alteração e dúvidas dos parlamentares. Assumir, responder, atender ou recusar não altera a emenda: qualquer mudança segue o fluxo de aprovação de sempre.'
            : 'Faça um pedido ou tire uma dúvida com a equipe da Prefeitura. Você acompanha tudo por aqui e recebe um e-mail quando houver resposta.'}
        </p>
        {podeTratar && podeCriar && (
          <div className="mt-4 flex gap-2" role="tablist">
            <button type="button" role="tab" aria-selected={abaAtiva === 'fila'} className={tab(abaAtiva === 'fila')} onClick={() => setAba('fila')}>
              Fila da equipe
            </button>
            <button type="button" role="tab" aria-selected={abaAtiva === 'minhas'} className={tab(abaAtiva === 'minhas')} onClick={() => setAba('minhas')}>
              Meus pedidos
            </button>
          </div>
        )}
      </section>

      {abaAtiva === 'fila' ? <FilaSolicitacoes /> : <MinhasSolicitacoes />}
    </div>
  );
};

export default SolicitacoesPage;
